import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Alert, Stack, Text } from '@solvhealth/jigsaw-v1';
import AutosuggestTextField from '../../../../../SolvPatternLibrary/AutosuggestTextField';
import { suggestionsTheme } from '../../constants';
import { EMPTY_STRING } from '../../../../../../core/util/string';
import {
  conditionallyToggleEmergencyMessage,
  getDefaultReasonForVisitForLocation,
} from '../../../../../../core/util/symptoms';
import SelectTextField from '../../../../../../components/SolvPatternLibrary/SelectTextField';
import {
  getReasonForVisitDropdownOptions,
  isReasonForVisitDropdownEnabled,
} from '../../../../../../core/util/location';
import BorderBoxReset from '~/components/util/BorderBoxReset';
import useNonBookableServiceWarning from '~/routes/book/hooks/useIsNonbookableService';

const StyledReasonForVisit = styled(AutosuggestTextField).withConfig({ componentId: 'sc-1juf5s0-0' })`
  width: 100%;
`;

type OwnProps = {
  form: any;
  errors: any;
  onBlur: (...args: any[]) => any;
  location: any;
  reasonForVisit?: string;
  isLoggedIn: boolean;
  suggestions: any[];
  fetchSuggestions: (...args: any[]) => any;
  onFieldChange: (...args: any[]) => any;
  autoFocus?: boolean;
  toggleEmergencyCopy: (...args: any[]) => any;
};

// @ts-expect-error ts-migrate(2456) FIXME: Type alias 'Props' circularly references itself.
type Props = OwnProps & typeof ReasonForVisit.defaultProps;

// @ts-expect-error ts-migrate(7022) FIXME: 'ReasonForVisit' implicitly has type 'any' because... Remove this comment to see the full error message
const ReasonForVisit = (props: Props) => {
  const {
    form,
    errors,
    onBlur,
    location,
    reasonForVisit,
    isLoggedIn,
    suggestions,
    fetchSuggestions,
    onFieldChange,
    autoFocus,
    toggleEmergencyCopy,
  } = props;

  const [rfvDraft, setRfvDraft] = useState(reasonForVisit);

  const {
    type: nonBookableServiceType,
    bodyText: nonBookableWarningBodyText,
    titleText: nonBookableWarningTitleText,
  } = useNonBookableServiceWarning(rfvDraft);

  // TODO: Should add check for appointment date loaded
  // onFieldChange should not be put into the dependency list, otherwise the render depth will overflow
  useEffect(() => {
    if (reasonForVisit) {
      onFieldChange(reasonForVisit, 'reasonForVisit');
      // Handles the RFV being changed externally from this component via prop, such as when a user clicks on a V1
      // service card
      setRfvDraft(reasonForVisit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonForVisit]);

  const handleBlur = () => {
    conditionallyToggleEmergencyMessage(form.getFieldValue('reasonForVisit'), toggleEmergencyCopy);
    onBlur();
  };

  const getInitialValueForReasonForVisit = () => {
    if (reasonForVisit) return reasonForVisit;
    if (isLoggedIn) return EMPTY_STRING;
    return getDefaultReasonForVisitForLocation(location);
  };

  const getSuggestions = () => {
    return suggestions
      ? suggestions.map((suggestion: any) => ({
          label: suggestion.label,
          value: suggestion.value,
        }))
      : [];
  };

  const onReasonForVisitTextInputChange = (e: any, suggestionSelected: any) => {
    fetchSuggestions(suggestionSelected.newValue);
    onFieldChange(suggestionSelected.newValue, 'reasonForVisit');
    setRfvDraft(suggestionSelected.newValue);
  };

  let hasError = false;
  if (errors) {
    hasError = !!errors.reasonForVisit;

    if (errors.errors) {
      hasError = !!errors.errors.reasonForVisit;
    }
  }

  if (isReasonForVisitDropdownEnabled(location)) {
    const options = getReasonForVisitDropdownOptions(location);
    return (
      <div>
        <label htmlFor="reasonForVisit">
          <Text mb={0.5} variant="subtitle2">
            Select a reason for visit
          </Text>
        </label>
        <SelectTextField
          {...form.getFieldProps('reasonForVisit', {
            rules: [{ required: true }],
          })}
          autoFocus={autoFocus}
          error={hasError}
          fullWidth
          id="reasonForVisit"
          onChange={({ target: { value } }: any) => onFieldChange(value, 'reasonForVisit')}
          selectOptions={[{ value: '', label: '' }, ...options]}
        />
      </div>
    );
  }

  return (
    <Stack mx="auto" space={2} width="100%">
      <div>
        <Text mb={0.5} variant="subtitle2">
          Reason for visit
        </Text>
        <StyledReasonForVisit
          {...form.getFieldProps('reasonForVisit', {
            rules: [{ required: true }],
            initialValue: getInitialValueForReasonForVisit(),
          })}
          autoFocus={autoFocus}
          error={hasError}
          fullWidth
          id="reason-for-visit"
          onBlur={handleBlur}
          onChange={onReasonForVisitTextInputChange}
          styleTheme={suggestionsTheme}
          suggestions={getSuggestions()}
        />
      </div>

      {!!nonBookableServiceType && (
        <BorderBoxReset>
          <Alert
            icon={null}
            show
            title={nonBookableWarningTitleText}
            tone="warning"
            variant="inline"
          >
            {nonBookableWarningBodyText}
          </Alert>
        </BorderBoxReset>
      )}
    </Stack>
  );
};

ReasonForVisit.defaultProps = {
  autoFocus: true,
};

export default ReasonForVisit;
